/**
 * ToxIntel Compliance Passport™ — brand-facing workspace.
 *
 * "Collect Once. Verify Once. Reuse Everywhere."
 *
 * Layout: 12-col enterprise-SaaS dashboard.
 *   • Header strip — brand identity + edit
 *   • Coverage cards — one per framework (Sephora Clean in MVP)
 *   • Evidence library — drag-drop upload + tagged list
 *   • Posture banner + retailer-export action
 *
 * Aesthetic: premium SaaS onboarding (not bureaucratic, not form-heavy).
 * Light theme to differentiate from the dark Bloomberg Copilot console —
 * Passport reads as "your operating dashboard", Copilot as "the war room".
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { SiteHeader } from '../components/SiteLayout';
import TI from '../components/brand/TI';
import { Button } from '../components/ui/button';
import {
  Certificate, CloudArrowUp, FilePdf, Trash, ShieldCheck,
  CheckCircle, Warning, ArrowRight, Tag, Sparkle, Pencil,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSTURE_THEME = {
  'No significant concern identified': { bg: '#ECFDF5', fg: '#065F46', border: '#10B981' },
  'Monitor':                            { bg: '#EFF6FF', fg: '#1E40AF', border: '#3B82F6' },
  'Further review recommended':         { bg: '#FFFBEB', fg: '#92400E', border: '#F59E0B' },
  'Escalation recommended':             { bg: '#FEF2F2', fg: '#9B1C1C', border: '#EF4444' },
};

export default function PassportPage() {
  const [profile, setProfile] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [activeFrameworkId, setActiveFrameworkId] = useState('sephora_clean');
  const [gap, setGap] = useState(null);
  const [bestMatch, setBestMatch] = useState(null);
  const [levels, setLevels] = useState([]);
  const [relianceNotice, setRelianceNotice] = useState('');
  const [editingBrand, setEditingBrand] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [p, fr, ev, vl, bm] = await Promise.all([
        axios.get(`${API_URL}/api/passport`, { withCredentials: true }),
        axios.get(`${API_URL}/api/passport/frameworks`, { withCredentials: true }),
        axios.get(`${API_URL}/api/passport/evidence`, { withCredentials: true }),
        axios.get(`${API_URL}/api/passport/verification-levels`),
        axios.get(`${API_URL}/api/passport/best-match`, { withCredentials: true }),
      ]);
      setProfile(p.data);
      setFrameworks(fr.data?.frameworks || []);
      setEvidence(ev.data?.evidence || []);
      setKinds(ev.data?.kinds || []);
      setLevels(vl.data?.levels || []);
      setRelianceNotice(vl.data?.reliance_notice || '');
      setBestMatch(bm.data);
    } catch (e) {
      toast.error('Could not load Passport');
    }
  }, []);

  const loadGap = useCallback(async (fid) => {
    try {
      const r = await axios.get(`${API_URL}/api/passport/gap-analysis/${fid}`, { withCredentials: true });
      setGap(r.data);
    } catch (e) { /* user may have no formulations — gap will just be empty */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (activeFrameworkId) loadGap(activeFrameworkId); }, [activeFrameworkId, evidence.length, loadGap]);

  const downloadExport = async () => {
    setExporting(true);
    try {
      // Programmatic same-tab anchor click → triggers a native download.
      const a = document.createElement('a');
      a.href = `${API_URL}/api/passport/export/${activeFrameworkId}`;
      a.download = `ToxIntel_Passport_${activeFrameworkId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Retailer export downloaded');
    } finally {
      setTimeout(() => setExporting(false), 1500);
    }
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <SiteHeader />
        <div className="max-w-4xl mx-auto px-6 py-12 text-sm text-[#6B7280]">Loading Passport…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6F8]" data-testid="passport-page">
      <SiteHeader />

      {/* Hero strip */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-baseline gap-3 mb-2">
            <Certificate size={20} className="text-[#E63946]" weight="duotone" />
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#4B5563]">
              <TI />&nbsp;Compliance Passport<sup className="text-[8px]">™</sup>
            </p>
          </div>
          <h1 className="text-3xl font-bold text-[#0B2447] tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>
            Collect Once. <span className="italic text-[#E63946]">Verify Once.</span> Reuse Everywhere.
          </h1>
          <p className="text-[13px] text-[#6B7280] mt-2 max-w-2xl">
            Evidence-supported scientific governance + retailer-readiness infrastructure.
            Upload supplier-backed documentation once — declarations, SDS, ingredient decks, packaging BOMs —
            then reuse across retailer onboarding, Hale Reviewed™, Hale Verified™, and regulatory workflows.
          </p>
        </div>
      </div>

      {/* Brand identity card */}
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <BrandIdentityCard
          profile={profile}
          editing={editingBrand}
          onEdit={() => setEditingBrand(true)}
          onSave={async (next) => {
            try {
              const r = await axios.post(`${API_URL}/api/passport`, next, { withCredentials: true });
              setProfile({ ...profile, ...r.data });
              setEditingBrand(false);
              toast.success('Brand identity saved');
            } catch (e) { toast.error('Could not save brand identity'); }
          }}
          onCancel={() => setEditingBrand(false)} />

        {/* Reliance notice — formal positioning, replaces "self-attestation". */}
        {relianceNotice && (
          <div className="border-l-4 border-[#0B2447] bg-white p-4 rounded-sm" data-testid="reliance-notice">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#0B2447] mb-1">
              Evidence-supported review · reliance scope
            </p>
            <p className="text-[11px] text-[#4B5563] leading-relaxed italic">{relianceNotice}</p>
          </div>
        )}

        {/* Best-match recommendation — harmonization engine made visible */}
        {bestMatch?.top_recommendation && (
          <BestMatchCard data={bestMatch} onPick={(fid) => setActiveFrameworkId(fid)} />
        )}

        {/* Framework coverage cards */}
        <section data-testid="passport-frameworks">
          <SectionHeader title="Framework coverage" subtitle="Reusable verification status across retailer & compliance frameworks" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {frameworks.map(f => (
              <FrameworkCard key={f.id} fw={f}
                active={f.id === activeFrameworkId}
                gap={f.id === activeFrameworkId ? gap : null}
                onSelect={() => setActiveFrameworkId(f.id)} />
            ))}
          </div>
        </section>

        {/* Active framework gap analysis */}
        {gap && <GapAnalysisPanel gap={gap} onExport={downloadExport} exporting={exporting} />}

        {/* Evidence library */}
        <section data-testid="passport-evidence-section">
          <SectionHeader title="Evidence library" subtitle={`${evidence.length} items · drag-drop a PDF, image, or declaration to add`} />
          <EvidenceUploader kinds={kinds} onUploaded={(e) => setEvidence(prev => [e, ...prev])} />
          <div className="mt-4 space-y-2">
            {evidence.length === 0 ? (
              <div className="p-8 border border-dashed border-gray-300 bg-white rounded-sm text-center" data-testid="evidence-empty">
                <CloudArrowUp size={28} className="mx-auto text-[#9CA3AF]" weight="duotone" />
                <p className="text-sm text-[#6B7280] mt-2">No evidence uploaded yet — start with a PFAS declaration or SDS.</p>
              </div>
            ) : evidence.map(e => (
              <EvidenceRow key={e.id} e={e}
                levels={levels}
                onLevelChange={async (newLevel) => {
                  try {
                    const r = await axios.patch(
                      `${API_URL}/api/passport/evidence/${e.id}`,
                      { verification_level: newLevel },
                      { withCredentials: true },
                    );
                    setEvidence(prev => prev.map(x => x.id === e.id ? r.data : x));
                    toast.success(`Verification tier · ${r.data.verification_level_label}`);
                  } catch (err) { toast.error('Could not update verification tier'); }
                }}
                onReclassify={async () => {
                  try {
                    const r = await axios.post(
                      `${API_URL}/api/passport/evidence/${e.id}/classify`,
                      {}, { withCredentials: true },
                    );
                    setEvidence(prev => prev.map(x => x.id === e.id ? r.data : x));
                    const ac = r.data?.auto_classification;
                    if (ac?.applied) {
                      toast.success(`Classified as ${r.data.kind_label} (${Math.round(ac.confidence * 100)}%)`);
                    } else {
                      toast.info(`Classifier unsure (${Math.round((ac?.confidence || 0) * 100)}%) — please re-tag manually`);
                    }
                  } catch (err) { toast.error('Classification failed'); }
                }}
                onDelete={async () => {
                  if (!window.confirm('Remove this evidence item?')) return;
                  try {
                    await axios.delete(`${API_URL}/api/passport/evidence/${e.id}`, { withCredentials: true });
                    setEvidence(prev => prev.filter(x => x.id !== e.id));
                    toast.success('Evidence removed');
                  } catch (err) { toast.error('Could not remove'); }
                }} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

// ---------- Sub-components ----------------------------------------------

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-3">
      <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-[#0B2447]">{title}</h2>
      {subtitle && <p className="text-xs text-[#6B7280] mt-0.5">{subtitle}</p>}
    </div>
  );
}

function BrandIdentityCard({ profile, editing, onEdit, onSave, onCancel }) {
  const [brand, setBrand] = useState(profile.brand_name || '');
  const [category, setCategory] = useState(profile.primary_category || '');
  const [subs, setSubs] = useState((profile.sub_categories || []).join(', '));
  useEffect(() => {
    setBrand(profile.brand_name || '');
    setCategory(profile.primary_category || '');
    setSubs((profile.sub_categories || []).join(', '));
  }, [profile]);

  return (
    <div className="bg-white border border-gray-100 rounded-sm p-5 flex items-center justify-between" data-testid="passport-brand-card">
      {editing ? (
        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
          <input value={brand} onChange={(e) => setBrand(e.target.value)}
            placeholder="Brand name"
            className="border border-gray-300 rounded-sm px-3 py-2 text-sm"
            data-testid="passport-brand-input" />
          <input value={category} onChange={(e) => setCategory(e.target.value)}
            placeholder="Primary category (skincare, color, fragrance…)"
            className="border border-gray-300 rounded-sm px-3 py-2 text-sm" />
          <input value={subs} onChange={(e) => setSubs(e.target.value)}
            placeholder="Sub-categories (comma-separated)"
            className="border border-gray-300 rounded-sm px-3 py-2 text-sm" />
        </div>
      ) : (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF]">Brand</p>
          <h3 className="text-xl font-bold text-[#0B2447]">{profile.brand_name}</h3>
          <p className="text-xs text-[#4B5563] mt-1">
            {profile.primary_category || 'No primary category set'}
            {(profile.sub_categories || []).length > 0 && ` · ${profile.sub_categories.join(' · ')}`}
          </p>
        </div>
      )}
      <div className="flex gap-2 flex-shrink-0">
        {editing ? (
          <>
            <Button variant="outline" onClick={onCancel} className="h-8 text-xs">Cancel</Button>
            <Button onClick={() => onSave({
              brand_name: brand,
              primary_category: category || null,
              sub_categories: subs.split(',').map(s => s.trim()).filter(Boolean),
            })} className="h-8 text-xs bg-[#0B2447] hover:bg-[#0B2447]/90 text-white"
              data-testid="passport-brand-save">Save</Button>
          </>
        ) : (
          <Button onClick={onEdit} variant="outline" className="h-8 text-xs" data-testid="passport-brand-edit">
            <Pencil size={12} className="mr-1" /> Edit
          </Button>
        )}
      </div>
    </div>
  );
}

function FrameworkCard({ fw, active, gap, onSelect }) {
  const pct = active ? gap?.coverage?.coverage_pct ?? 0 : null;
  return (
    <button onClick={onSelect}
      className={`text-left bg-white border-2 rounded-sm p-4 transition-all ${active ? 'border-[#E63946] shadow-sm' : 'border-gray-100 hover:border-gray-300'}`}
      data-testid={`framework-card-${fw.id}`}>
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm font-bold text-[#0B2447]">{fw.name}</h3>
        <span className="text-[10px] font-mono text-[#9CA3AF]">v{fw.version}</span>
      </div>
      <p className="text-[11px] text-[#6B7280] leading-relaxed line-clamp-3 mb-3">{fw.description}</p>
      <div className="flex items-baseline gap-3 text-[10px] font-mono text-[#4B5563]">
        <span>{fw.evidence_required_count} required</span>
        <span>+{fw.evidence_optional_count} optional</span>
        <span>{fw.restricted_ingredient_count} substances</span>
      </div>
      {active && pct !== null && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Coverage</span>
            <span className="text-lg font-bold text-[#0B2447]">{Math.round(pct)}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#E63946]" style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
        </div>
      )}
    </button>
  );
}

function GapAnalysisPanel({ gap, onExport, exporting }) {
  const theme = POSTURE_THEME[gap.posture] || POSTURE_THEME.Monitor;
  return (
    <section data-testid="gap-analysis-panel">
      <SectionHeader title={`${gap.framework.name} · gap analysis`}
        subtitle={`${gap.evidence_count} evidence items · ${gap.formulations_scanned} formulations scanned · v${gap.framework.version}`} />
      <div className="rounded-sm overflow-hidden border-l-4"
        style={{ background: theme.bg, borderLeftColor: theme.border }}>
        <div className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: theme.fg }}>
              Status
            </p>
            <p className="text-xl font-bold mt-0.5" style={{ color: theme.fg }} data-testid="gap-posture">
              {gap.posture}
            </p>
          </div>
          <Button onClick={onExport} disabled={exporting}
            className="bg-[#0B2447] hover:bg-[#0B2447]/90 text-white text-xs h-9"
            data-testid="export-passport-btn">
            <FilePdf size={14} className="mr-1.5" />
            {exporting ? 'Generating…' : 'Generate retailer export'}
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-3">
        <div className="bg-white border border-gray-100 rounded-sm p-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[#0B2447] mb-2">Required evidence checklist</h4>
          <ul className="space-y-1.5">
            {gap.coverage.items.filter(i => i.required).map(it => (
              <li key={it.requirement_id} className="flex items-baseline gap-2 text-[12px]" data-testid={`req-${it.requirement_id}`}>
                {it.satisfied
                  ? <CheckCircle size={13} className="text-[#10B981] flex-shrink-0" weight="fill" />
                  : <Warning size={13} className="text-[#F59E0B] flex-shrink-0" weight="fill" />}
                <span className={it.satisfied ? 'text-[#4B5563] line-through' : 'text-[#0B2447] font-semibold'}>
                  {it.label}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border border-gray-100 rounded-sm p-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[#0B2447] mb-2">Restricted-substance scan</h4>
          {gap.ingredient_conflicts.length === 0 ? (
            <p className="text-xs text-[#10B981] flex items-center gap-1.5">
              <CheckCircle size={13} weight="fill" /> No restricted-substance matches across your formulations.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {gap.ingredient_conflicts.map((c, i) => (
                <li key={i} className="text-[12px]" data-testid={`conflict-${c.inci}`}>
                  <span className="font-bold text-[#0B2447]">{c.inci}</span>
                  <span className={`ml-2 text-[10px] uppercase tracking-wider font-bold ${
                    c.status === 'prohibited' ? 'text-[#9B1C1C]'
                      : c.status === 'watchlist' ? 'text-[#92400E]' : 'text-[#1E40AF]'}`}>
                    {c.status.replace('_', ' ')}
                  </span>
                  <p className="text-[11px] text-[#6B7280] mt-0.5 font-mono">
                    matched: {c.matched_ingredients.slice(0, 3).join(', ')}
                    {c.matched_ingredients.length > 3 && `, +${c.matched_ingredients.length - 3} more`}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function EvidenceUploader({ kinds, onUploaded }) {
  const [kind, setKind] = useState('pfas_declaration');
  const [title, setTitle] = useState('');
  const [claims, setClaims] = useState('');
  const [autoClassify, setAutoClassify] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', autoClassify ? 'other' : kind);
    if (title.trim()) fd.append('title', title.trim());
    if (claims.trim()) fd.append('claims', claims.trim());
    if (autoClassify) fd.append('auto_classify', 'true');
    try {
      const r = await axios.post(`${API_URL}/api/passport/evidence`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const ac = r.data?.auto_classification;
      if (ac?.applied) {
        toast.success(`Auto-classified · ${r.data.kind_label} (${Math.round(ac.confidence * 100)}% confidence)`,
          { duration: 5000 });
      } else if (ac && !ac.applied) {
        toast.info(`Classifier unsure (${Math.round(ac.confidence * 100)}%) — please verify the kind manually`,
          { duration: 6000 });
      } else {
        toast.success(`Uploaded · ${r.data.kind_label}`);
      }
      setTitle(''); setClaims('');
      onUploaded(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-sm p-4">
      {/* Auto-classify toggle */}
      <label className="flex items-center gap-2 mb-3 text-xs cursor-pointer select-none">
        <input type="checkbox" checked={autoClassify}
          onChange={(e) => setAutoClassify(e.target.checked)}
          className="rounded border-gray-300 text-[#E63946] focus:ring-[#E63946]"
          data-testid="evidence-auto-classify-toggle" />
        <Sparkle size={13} className="text-[#E63946]" weight="duotone" />
        <span className="font-bold text-[#0B2447]">Auto-classify with Claude vision</span>
        <span className="text-[#6B7280]">— drop a PDF or image, the platform tags it automatically</span>
      </label>
      <div className={`grid md:grid-cols-3 gap-3 mb-3 ${autoClassify ? 'opacity-50 pointer-events-none' : ''}`}>
        <select value={kind} onChange={(e) => setKind(e.target.value)}
          className="border border-gray-300 rounded-sm px-3 py-2 text-sm"
          disabled={autoClassify}
          data-testid="evidence-kind-select">
          {kinds.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
        </select>
        <input value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (optional)"
          className="border border-gray-300 rounded-sm px-3 py-2 text-sm"
          disabled={autoClassify}
          data-testid="evidence-title-input" />
        <input value={claims} onChange={(e) => setClaims(e.target.value)}
          placeholder="Tags (comma-sep: pfas_free, supplier)"
          className="border border-gray-300 rounded-sm px-3 py-2 text-sm"
          disabled={autoClassify}
          data-testid="evidence-claims-input" />
      </div>
      <div className="border-2 border-dashed border-gray-300 rounded-sm p-6 text-center hover:border-[#E63946] transition-colors cursor-pointer"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
        data-testid="evidence-dropzone">
        <CloudArrowUp size={24} className="mx-auto text-[#9CA3AF]" weight="duotone" />
        <p className="text-xs text-[#4B5563] mt-2">
          {uploading
            ? (autoClassify ? 'Analysing & classifying…' : 'Uploading…')
            : 'Drop a file here or click to browse · PDF, image, declaration (≤25 MB)'}
        </p>
      </div>
      <input ref={fileRef} type="file" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

function EvidenceRow({ e, onDelete, onReclassify, onLevelChange, levels = [] }) {
  const [reclassifying, setReclassifying] = React.useState(false);
  const ac = e.auto_classification;
  const lvl = e.verification_level || 1;
  const lvlColor = { 1: '#6B7280', 2: '#1E40AF', 3: '#065F46', 4: '#0B2447' }[lvl] || '#6B7280';
  return (
    <div className="bg-white border border-gray-100 rounded-sm p-3 flex items-center gap-3" data-testid={`evidence-row-${e.id}`}>
      <div className="w-9 h-9 rounded-sm bg-[#FDE8E8] flex items-center justify-center flex-shrink-0">
        <FilePdf size={16} className="text-[#E63946]" weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <p className="text-sm font-semibold text-[#0B2447] truncate">{e.title}</p>
          <span className="text-[10px] font-mono uppercase tracking-wider text-[#9CA3AF] flex-shrink-0">
            {e.kind_label}
          </span>
          {ac?.applied && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-[#E63946]">
              <Sparkle size={9} weight="fill" /> auto · {Math.round((ac.confidence || 0) * 100)}%
            </span>
          )}
          {/* Verification-level chip with click-to-cycle */}
          <button onClick={() => {
            const next = lvl >= 4 ? 1 : lvl + 1;
            onLevelChange?.(next);
          }}
            className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border"
            style={{ color: lvlColor, borderColor: lvlColor }}
            title="Click to cycle verification confidence tier"
            data-testid={`evidence-level-${e.id}`}>
            L{lvl} · {e.verification_level_label || levels[lvl - 1]?.label || 'Brand-Submitted'}
          </button>
        </div>
        <p className="text-[11px] text-[#6B7280] font-mono mt-0.5 truncate">
          {e.filename} · {e.size ? `${Math.round(e.size / 1024)} KB` : '—'} · {new Date(e.created_at).toLocaleDateString()}
          {e.expires_at && ` · expires ${e.expires_at}`}
        </p>
        {(e.claims || []).length > 0 && (
          <div className="flex gap-1 mt-1.5 flex-wrap">
            {e.claims.map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#F3F4F6] text-[#4B5563] rounded">
                <Tag size={9} weight="bold" />{c}
              </span>
            ))}
          </div>
        )}
      </div>
      {!ac?.applied && (
        <button onClick={async () => {
          setReclassifying(true);
          try { await onReclassify(); } finally { setReclassifying(false); }
        }}
          disabled={reclassifying}
          className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-[#E63946] hover:bg-[#FDE8E8] disabled:opacity-50"
          data-testid={`evidence-classify-${e.id}`}
          title="Auto-classify with Claude vision">
          <Sparkle size={11} className="inline mr-0.5" weight="bold" />
          {reclassifying ? '…' : 'Classify'}
        </button>
      )}
      <button onClick={onDelete} className="p-2 text-[#9CA3AF] hover:text-[#9B1C1C]"
        data-testid={`evidence-delete-${e.id}`}>
        <Trash size={14} />
      </button>
    </div>
  );
}

function BestMatchCard({ data, onPick }) {
  const top = data.top_recommendation;
  const others = data.rankings.slice(1, 5);
  return (
    <section className="bg-gradient-to-br from-[#0B2447] to-[#19376D] text-white rounded-sm p-5" data-testid="best-match-card">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} weight="duotone" className="text-[#E63946]" />
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60">
            Best-match framework
          </p>
        </div>
        <p className="text-[10px] font-mono text-white/40">
          {data.evidence_count} evidence · {data.formulations_scanned} formulations scanned
        </p>
      </div>
      <div className="flex items-baseline gap-3 mb-2">
        <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>
          {top.framework.name}
        </h3>
        <span className="text-[10px] font-mono text-white/40">v{top.framework.version}</span>
      </div>
      <p className="text-[13px] text-white/80 mb-4 max-w-2xl">
        Of the {data.rankings.length} retailer overlays in your Passport, you're closest to readiness for{' '}
        <span className="font-bold text-white">{top.framework.name}</span> — readiness score{' '}
        <span className="font-bold text-[#E63946]">{top.readiness_score.toFixed(1)}</span>,{' '}
        coverage {top.coverage_pct.toFixed(1)}%, posture <i>{top.posture}</i>.
      </p>
      <div className="flex items-end gap-4 flex-wrap">
        <button onClick={() => onPick(top.framework.id)}
          className="px-4 py-2 bg-[#E63946] hover:bg-[#C9192C] text-black text-[11px] uppercase tracking-wider font-bold rounded-sm"
          data-testid="best-match-pick-btn">
          Open gap analysis →
        </button>
        <div className="flex-1 flex gap-2 flex-wrap min-w-0">
          {others.map(r => (
            <button key={r.framework.id} onClick={() => onPick(r.framework.id)}
              className="px-2.5 py-1.5 border border-white/15 hover:border-[#E63946] text-[10px] uppercase tracking-wider text-white/70 hover:text-white"
              data-testid={`best-match-rank-${r.framework.id}`}>
              {r.framework.name} · {r.coverage_pct.toFixed(0)}%
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
